import { Dates } from "../../util";
import { WorkflowData, WorkflowEvent, WorkerAccount, WorkerListItem, AvatarPictureType, EmploymentRelationSalaryPaymentMethod, AgeRange, LegalEntityType } from "../../model";
import { Ajax, CrudApiBase } from "../infra";

/**
 * Provides CRUD access to Workers (employees) created by the employer
 * or to whom the employer has paid salaries.
 */
export class Workers extends CrudApiBase<WorkerAccount, WorkerListItem> {
  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  /** Base URL for details etc. */
  protected baseUrl = "/v02/api/accounts/workers";

  constructor(ajax: Ajax) {
    super(ajax);
  }

  /** Gets the OData URL */
  public getODataUrl(): string {
    return "/v03-rc/api/accounts/workers";
  }

  /** Client-side (synchronous) method for getting a new blank item as bases for UI binding. */
  public getBlank(): WorkerAccount {
    return {
      avatar: {
        pictureType: AvatarPictureType.Icon,
      },
      employment: {
        irIncomeEarnerTypes: [],
        holidays: {},
        usecase: {
          data: {},
        },
        work: {
          salaryDefaults: [],
        },
        salaryPaymentMethod: EmploymentRelationSalaryPaymentMethod.BankAccount
      },
      contact: {},
      ageRange: AgeRange.Age18_52,
      entityType: LegalEntityType.PersonCreatedByEmployer,
      id: null,
      createdAt: Dates.getToday(),
      updatedAt: Dates.getToday(),
      isReadOnly: null,
    };
  }

  /** Saves the Worker account ot the storage */
  public save(itemToSave: WorkerAccount): Promise<WorkerAccount> {
    return super.save(itemToSave);
  }

  /**
   * Adds/updates the workflow event for the item.
   *
   * @param item - Item for which to set the workflow event.
   * @param wfEvent - Event to add or update.
   * @returns Workflow data for the item.
   */
  public saveWorkflowEvent(item: WorkerAccount, wfEvent: WorkflowEvent): Promise<WorkflowData> {
    const id = item.employmentId;
    return this.ajax.postJSON(`/v03/api/employments/${id}/workflows`, wfEvent);
  }

  /**
   * Deletes the workflow item or items from the item.
   * @param item - Item for which to delete workflow events.
   * @param wfIdOrType - Event id or type.
   * @returns A Promise with result data "Object deleted".
   */
  public deleteWorkflowEvent(item: WorkerAccount, wfIdOrType: string): Promise<string> {
    const id = item.employmentId;
    return this.ajax.remove(`/v03/api/employments/${id}/workflows/${wfIdOrType}`);
  }

/**
 * Gets a single workflow event for the item.
 * @param item - Item for which to get the single event.
 * @param wfId - Event id or type.
 * @returns A Promise with event data".
 */
  public getWorkflowEvent(item: WorkerAccount, wfId: string): Promise<WorkflowEvent> {
    const id = item.employmentId;
    return this.ajax.getJSON(`/v03/api/employments/${id}/workflows/events/${wfId}`);
  }
}
